import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import { useUsers } from '@/hooks/data/useUsers';
import { Button } from '@/components/ui/Button';
import { PlusIcon } from '@/components/icons';
import { ErrorAlert, PageHeader } from '@/components/PageParts';
import { UserModal } from '@/components/UserModal';
import { UsersTable } from '@/components/users/UsersTable';
import { pluralize } from '@/utils/format';
import type { User } from '@/types/user';
import './Users.css';

/**
 * Users page component (admin only)
 *
 * Features:
 * - List all users with role and status indicators
 * - Add, edit (role and status) and delete users
 * - Cannot modify own account's role or status
 */
export function Users() {
  const { user: currentUser } = useAuth();
  const { users, isLoading, error, reload, createUser, updateUser, deleteUser } = useUsers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const openModal = (user: User | null) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = (user: User) => {
    void confirm({
      title: 'Delete User',
      message: (
        <>
          Are you sure you want to delete <strong>{user.email}</strong>? This action cannot be undone
          and will remove all their access to the system.
        </>
      ),
      confirmLabel: 'Delete',
      onConfirm: () => deleteUser(user.id),
      errorMessage: 'Failed to delete user',
    });
  };

  return (
    <div className="users-page">
      <PageHeader
        classPrefix="users"
        title="Users"
        count={pluralize(users.length, 'user')}
        actions={
          <Button leftIcon={<PlusIcon />} onClick={() => openModal(null)}>
            Add User
          </Button>
        }
      />

      <ErrorAlert classPrefix="users" message={error} onRetry={() => void reload()} />

      <UsersTable
        users={users}
        isLoading={isLoading}
        currentUserId={currentUser?.id}
        onAddFirst={() => openModal(null)}
        onEdit={openModal}
        onDelete={handleDelete}
      />

      <UserModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onCreate={createUser}
        onUpdate={updateUser}
        user={selectedUser}
        currentUserId={currentUser?.id}
      />

      {confirmDialog}
    </div>
  );
}
